import { useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  ChevronDownIcon,
  Dialog,
  DropdownMenu,
  FolderPlusIcon,
  IconButton,
  LockIcon,
  PlusIcon,
} from '@buffer-mono/popcorn'

import { ExploreFeedsDialogDropdownMenuItem } from '~publish/pages/Create/Feed/components/ExploreFeedsDialog/ExploreFeedsDialogDropdownMenuItem'
import { FreeFeedsLimitReachedDialog } from '~publish/pages/Create/Feed/components/FreeFeedsLimitReachedDialog/FreeFeedsLimitReachedDialog'
import { HardLimitReachedDialog } from '~publish/pages/Create/Feed/components/HardLimitReachedDialog/HardLimitReachedDialog'
import { ImportFeedsDialogDropdownMenuItem } from '~publish/pages/Create/Feed/components/ImportFeedsDialog/ImportFeedsDialogDropdownMenuItem'
import { SubscribeToFeedDialogContent } from '~publish/pages/Create/Feed/components/SubscribeToFeedDialog/SubscribeToFeedDialog'
import type { FeedCTA } from '~publish/pages/Create/Feed/constants/constants'
import type { WrappedCreateFeedResponse } from '~publish/pages/Create/Feed/hooks/useCreateFeed'

import styles from './AddFeedDropdownMenu.module.css'

interface AddFeedDropdownMenuProps {
  tier: 'free' | 'paid'
  limitReached: boolean
  collectionsLimitReached: boolean
  totalRemainingFeeds: number
  existingFeeds: { id: string; url: string }[]
  onAddFeed: (args: {
    feedUrl: string
    cta: FeedCTA
  }) => Promise<WrappedCreateFeedResponse>
  onDeleteFeed: (feedId: string) => Promise<void>
  onUpgrade: (args: { cta: CTA; upgradePathName: string }) => void
  onAddCollection: () => void
}

/**
 * Split button for adding feeds: the main button runs the primary Subscribe to
 * Feed action, and the chevron opens a menu with the other add options.
 */
export function AddFeedDropdownMenu({
  tier,
  limitReached,
  collectionsLimitReached,
  totalRemainingFeeds,
  existingFeeds,
  onAddFeed,
  onDeleteFeed,
  onUpgrade,
  onAddCollection,
}: AddFeedDropdownMenuProps): JSX.Element {
  return (
    <Button.Group variant="secondary" size="medium">
      <SubscribeToFeedButton
        tier={tier}
        limitReached={limitReached}
        existingFeeds={existingFeeds}
        onAddFeed={onAddFeed}
        onUpgrade={onUpgrade}
      />
      <DropdownMenu modal={false}>
        <DropdownMenu.Trigger>
          <IconButton label="More feed options">
            <ChevronDownIcon />
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          onCloseAutoFocus={(e): void => e.preventDefault()}
        >
          <ImportFeedsDialogDropdownMenuItem
            tier={tier}
            limitReached={limitReached}
            existingFeeds={existingFeeds}
            remainingFeedsCount={totalRemainingFeeds}
            type="feeds"
            onAddFeed={onAddFeed}
            onRemoveFeed={onDeleteFeed}
            onUpgrade={onUpgrade}
          />
          <DropdownMenu.Separator />
          <ExploreFeedsDialogDropdownMenuItem
            existingFeeds={existingFeeds}
            tier={tier}
            limitReached={limitReached}
            type="feeds"
            onAddFeed={onAddFeed}
            onRemoveFeed={onDeleteFeed}
            onUpgrade={onUpgrade}
          />
          <DropdownMenu.Separator />
          <HardLimitReachedDialog
            enabled={collectionsLimitReached}
            type="collections"
            tier={tier}
          >
            <DropdownMenu.Item
              onSelect={(event): void => {
                if (collectionsLimitReached) event.preventDefault()
                else onAddCollection()
              }}
            >
              <FolderPlusIcon aria-hidden /> New Collection
              {collectionsLimitReached && (
                <LockIcon className={styles.suffixIcon} />
              )}
            </DropdownMenu.Item>
          </HardLimitReachedDialog>
        </DropdownMenu.Content>
      </DropdownMenu>
    </Button.Group>
  )
}

type SubscribeToFeedButtonProps = Pick<
  AddFeedDropdownMenuProps,
  'tier' | 'limitReached' | 'existingFeeds' | 'onAddFeed' | 'onUpgrade'
>

function SubscribeToFeedButton({
  tier,
  limitReached,
  existingFeeds,
  onAddFeed,
  onUpgrade,
}: SubscribeToFeedButtonProps): JSX.Element {
  const [open, setOpen] = useState(false)

  const handleUpgradeClick = (): void => {
    onUpgrade({
      cta: 'create-feeds-upgradeDialog-upgradeButton-1',
      upgradePathName: 'feeds-subscribeToFeed-upgrade',
    })
    setOpen(false)
  }

  return (
    <>
      <Button onClick={(): void => setOpen(true)}>
        <PlusIcon />
        New Feed
      </Button>
      <HardLimitReachedDialog
        enabled={limitReached && tier === 'paid'}
        type="feeds"
        tier={tier}
        open={open}
        onOpenChange={setOpen}
      />
      <FreeFeedsLimitReachedDialog
        enabled={limitReached && tier === 'free'}
        open={open}
        onOpenChange={setOpen}
        onUpgrade={handleUpgradeClick}
      />
      {!limitReached && (
        <Dialog open={open} onOpenChange={setOpen}>
          <Dialog.Portal>
            <SubscribeToFeedDialogContent
              existingFeeds={existingFeeds}
              onAddFeed={onAddFeed}
              onOpenChange={setOpen}
            />
          </Dialog.Portal>
        </Dialog>
      )}
    </>
  )
}
